import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import CommonBubble from '@home/components/CommonBubble';
import { getName } from '@home/utils/common';
import SvgIcon from '@home/components/SvgIcon';
import VsBenchmark from '../VsBenchmark';
import { useIntl, useSelector } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import './index.scss';
import { DB_DASHBOARD_LOCAL_VS_BENCHMARK_RED_DOT_IS_IN_STORAGE_KEY } from '@/pages/dashboard/_config/constant';
import { useRedDot } from '@/pages/dashboard/hooks/useRedDot';
import classNames from 'classnames';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import componentMap from '@/pages/dashboard/_config/compConfig';

export default function VsBenchmarkBtn(props: any) {
  const {
    benchmarkConfig,
    hasVs,
    componentMetric,
    bubbleContent,
    bubbleSlot,
    data,
    ...restProps
  } = props;
  const {
    textEn = 'Benchmark',
    textCn = '行业基准',
    redDotConfig = {
      enabled: false,
    },
    buttonId: legacyButtonId = '',
    trackingConfig = {},
    logConfig: benchmarkLogConfig = {},
    bubblePlacement,
    bubbleDark = false,
    bubbleClassName,
  } = benchmarkConfig || {};
  const { enabled: trackingEnabled = false, interval = 3000 } =
    trackingConfig || {};
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const pageLogConfig = restProps?.logConfig || {};

  const mergedLogConfig = useMemo(
    () => ({
      ...pageLogConfig,
      ...benchmarkLogConfig,
      buttonId:
        benchmarkLogConfig.buttonId ||
        legacyButtonId ||
        pageLogConfig.buttonId ||
        '',
    }),
    [benchmarkLogConfig, legacyButtonId, pageLogConfig],
  );

  const { pageId, buttonId } = mergedLogConfig;

  // 计时器相关
  const observationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 从 Redux 状态获取游戏信息
  const { currentGame } = useSelector((state: any) => ({
    currentGame: state?.dashboard?.currentGame,
  }));

  // 使用红点 hook 管理红点显示和消失
  const { shouldShowRedDot, handleRedDotDisappear } = useRedDot({
    showRedDot: redDotConfig?.enabled,
    storageKey: redDotConfig?.storageKey || DB_DASHBOARD_LOCAL_VS_BENCHMARK_RED_DOT_IS_IN_STORAGE_KEY,
    pageId,
    buttonId,
  });

  // 清理计时器
  const clearObservationTimer = (): void => {
    if (observationTimerRef.current) {
      clearTimeout(observationTimerRef.current);
      observationTimerRef.current = null;
    }
  };

  // 进行打点上报
  const reportObservation = useCallback((): void => {
    buriedBtnPoint({
      buttonName: 'VsBenchmarkBtn',
      uidType: 'game_code',
      uid: currentGame?.game_code || '',
      gameName: currentGame?.game_name || currentGame?.game_cn_name || '',
      ...mergedLogConfig,
    });
  }, [currentGame, mergedLogConfig]);

  const startTrackingTimer = useCallback((): void => {
    if (!trackingEnabled) {
      return;
    }
    clearObservationTimer();
    observationTimerRef.current = setTimeout(() => {
      reportObservation();
    }, interval);
  }, [interval, reportObservation, trackingEnabled]);

  const stopTrackingTimer = useCallback((): void => {
    clearObservationTimer();
  }, []);

  useEffect(() => {
    return () => {
      stopTrackingTimer();
    };
  }, [stopTrackingTimer]);

  const renderBubbleSlot = () => {
    if (!bubbleSlot?.name) {
      return null;
    }
    const BubbleSlotCom = componentMap?.[bubbleSlot.name];
    if (!BubbleSlotCom) {
      return null;
    }
    const { props: slotProps, ...slotRest } = bubbleSlot;
    return (
      <BubbleSlotCom
        {...slotRest}
        {...(slotProps || {})}
        {...restProps}
        data={data}
        logConfig={mergedLogConfig}
      />
    );
  };

  const hasCustomBubble = !!(bubbleContent || bubbleSlot?.name);

  const bubbleNode =
    bubbleContent ??
    renderBubbleSlot() ??
    (
      <VsBenchmark
        {...benchmarkConfig}
        {...restProps}
        data={data}
        metricColumn={componentMetric}
      />
    );

  return hasVs ? (
    <CommonBubble
      className={classNames(
        hasCustomBubble
          ? 'dbd-key-metric-benchmark-bubble'
          : 'dbd-overlay-card-right-bubble-container',
        bubbleClassName,
      )}
      placement={bubblePlacement}
      dark={bubbleDark}
      content={bubbleNode}
      onVisibleChange={(visible) => {
        if (visible) {
          handleRedDotDisappear();
          startTrackingTimer();
        } else {
          stopTrackingTimer();
        }
      }}
    >
      <div
        className={classNames('dbd-overlay-card-right-solt', {
          'dbd-overlay-card-right-solt-red-dot-wrapper': shouldShowRedDot,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <SvgIcon type="vs-new" />
        {getName(isEn, textEn, textCn)}
        {shouldShowRedDot && (
          <div className="dbd-overlay-card-right-solt-red-dot" />
        )}
      </div>
    </CommonBubble>
  ) : null;
}
